import CloseOutlined from '@mui/icons-material/CloseOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { Stack } from '@mui/system';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { DealPromptContextType } from '../utils/types.ts';

export type DealPromptEditPromptDialogProps = {
  open: boolean;
  onClose: () => void;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onRegenerate: () => void;
  contextType: DealPromptContextType;
};

export const DealPromptEditPromptDialog = ({
  open,
  onClose,
  prompt,
  onPromptChange,
  onRegenerate,
  contextType,
}: DealPromptEditPromptDialogProps) => {
  const { t } = useTranslation();
  const initialPromptRef = useRef(prompt);

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs only when opened, uses initial prompt only (prompt changes ignored intentnionally)
  useEffect(() => {
    if (open) {
      initialPromptRef.current = prompt;
    }
  }, [open]);

  const handleRegenerate = useCallback(() => {
    onRegenerate();
    onClose();
  }, [onRegenerate, onClose]);

  const handleCancel = useCallback(() => {
    // We need to trigger actual prompt change so fetchResults would get updated for submition
    onPromptChange(initialPromptRef.current);
    onClose();
  }, [onPromptChange, onClose]);

  const isRegenerateDisabled = prompt === initialPromptRef.current;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: 5,
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {t('Edit Prompt')}
          <IconButton onClick={handleCancel}>
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pb: 0, px: 2 }}>
        <TextField
          fullWidth
          autoFocus
          value={prompt}
          multiline
          onChange={e => onPromptChange(e.target.value)}
          slotProps={{ htmlInput: { 'data-test': 'edit-prompt-input' } }}
          sx={{
            '& .MuiInputBase-root': {
              paddingX: 1.5,
              paddingY: 0.5,
            },
          }}
        />
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('Changing the search phrase won’t reset your selected filters, tags, or match logic.')}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button variant="text" color="primary" onClick={handleCancel} data-test="edit-prompt-cancel-button">
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isRegenerateDisabled}
          onClick={handleRegenerate}
          data-test="edit-prompt-regenerate-button"
          data-pendo-feature={`deal-prompt-main-${contextType}-regenerate`}
        >
          {t('REGENERATE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
